import * as std from '@nx/stdlib';
import { cloneDeep } from 'lodash-es';
import type { Neo4jError } from 'neo4j-driver';

import { formatDescriptionFromGqlStatusDescription, formatTitleFromGqlStatusDescription } from './gql-formatters';

const ERROR_TYPES = ['Neo4jError', 'LegacyNeo4jError', 'SerializedError', 'UnknownError'] as const;
type ErrorType = (typeof ERROR_TYPES)[number];

export type ParseableError =
  | Neo4jError
  | Pick<Neo4jError, 'name' | 'message' | 'code'>
  | Pick<Neo4jError, 'name' | 'message'>;

export type FormattedNeo4jError = {
  code?: string;
  title?: string;
  description?: string;
  documentationUrl?: string;
  originalError?: {
    gqlStatus?: string;
    gqlStatusDescription?: string;
  };
  innerError?: Error;
};

export interface InvalidUrlConnectionError {
  error: Neo4jError;
  discoveryUrls: string[];
}

export type SerializableCause = {
  gqlStatus: string | undefined;
  gqlStatusDescription: string | undefined;
  cause: SerializableCause | undefined;
};

const parseErrorType = (error: unknown): ErrorType => {
  if (error !== null && typeof error === 'object') {
    if ('gqlStatus' in error && 'gqlStatusDescription' in error) {
      return 'Neo4jError';
    }

    if ('name' in error && 'message' in error && 'code' in error) {
      return 'LegacyNeo4jError';
    }

    if ('name' in error && 'message' in error) {
      return 'SerializedError';
    }
  }

  return 'UnknownError';
};

export const isParseableError = (error: unknown): error is ParseableError => {
  return parseErrorType(error) !== 'UnknownError';
};

const isGqlNeo4jError = (error: unknown): error is Neo4jError => {
  return parseErrorType(error) === 'Neo4jError';
};

const isLegacyNeo4jError = (error: unknown): error is Pick<Neo4jError, 'name' | 'message' | 'code'> => {
  return parseErrorType(error) === 'LegacyNeo4jError';
};

export const isNeo4jError = (error: unknown): error is Neo4jError => {
  return isGqlNeo4jError(error) || isLegacyNeo4jError(error);
};

export const isInvalidUrlConnectionError = (error: unknown): error is InvalidUrlConnectionError => {
  if (error !== null && typeof error === 'object') {
    return 'error' in error && 'discoveryUrls' in error;
  }

  return false;
};

export const getGqlStatusDocumentationUrl = (gqlStatus: string): string =>
  `https://neo4j.com/docs/status-codes/current/errors/gql-errors/${gqlStatus}/`;

const formatNeo4jErrorGqlStatusObject = (error: Neo4jError): FormattedNeo4jError => {
  const gqlStatusTitle = formatTitleFromGqlStatusDescription(error.gqlStatusDescription);
  const { gqlStatus, gqlStatusDescription } = error;
  const description = formatDescriptionFromGqlStatusDescription(error.gqlStatusDescription);
  const title = std.isNonEmptyString(gqlStatusTitle) ? gqlStatusTitle : description;

  return {
    title: std.isNonEmptyString(title) ? `${gqlStatus}: ${title}` : gqlStatus,
    description,
    ...(gqlStatus && {
      documentationUrl: getGqlStatusDocumentationUrl(gqlStatus),
    }),
    originalError: {
      gqlStatus,
      gqlStatusDescription,
    },
    innerError: error.cause,
  };
};

const formatNeo4jError = (error: Neo4jError): FormattedNeo4jError => {
  const { code, code: title, message: description } = error;

  return {
    code,
    title,
    description,
  };
};

const formatParseableError = (error: ParseableError, showGqlErrorsAndNotifications: boolean): FormattedNeo4jError => {
  if (showGqlErrorsAndNotifications && isGqlNeo4jError(error)) {
    return formatNeo4jErrorGqlStatusObject(error);
  }

  if (isNeo4jError(error)) {
    return formatNeo4jError(error);
  }

  return { title: error.name, description: error.message };
};

export const formatError = (error: unknown, showGqlErrorsAndNotifications = false): FormattedNeo4jError | undefined => {
  return isParseableError(error) ? formatParseableError(error, showGqlErrorsAndNotifications) : undefined;
};

const serializeCause = (cause: Error | undefined): SerializableCause | undefined => {
  if (cause !== undefined && isNeo4jError(cause)) {
    return {
      gqlStatus: cause.gqlStatus,
      gqlStatusDescription: cause.gqlStatusDescription,
      cause: serializeCause(cause.cause),
    };
  }

  return undefined;
};

/**
 * Serializes a Neo4jError and its nested causes, making it safe for use in redux.
 */
export const getSerializableNeo4jError = (error: Neo4jError) => ({
  name: error.name,
  code: error.code,
  message: error.message,
  stack: error.stack,
  gqlStatus: error.gqlStatus,
  gqlStatusDescription: error.gqlStatusDescription,
  cause: serializeCause(error.cause),
});

export const getDeepestNeo4jError = (error: ParseableError): Neo4jError | undefined => {
  if (isNeo4jError(error)) {
    if (error.cause !== undefined) {
      return getDeepestNeo4jError(error.cause);
    }

    return error;
  }

  return undefined;
};

/**
 * Flattens a Neo4jError to an array of Neo4jErrors from its nested causes.
 * The order of the array is then reversed, from the deepest error to the topmost error.
 * @param error The error to flatten and reverse
 * @returns Reversed array of flattened Neo4jErrors
 */
export const flattenAndInvertNeo4jErrors = (error: ParseableError): ParseableError[] => {
  const flattenErrorsToArray = (currentError: ParseableError, errors: ParseableError[] = []) => {
    const causeIsNeo4jError = isNeo4jError(currentError) && isNeo4jError(currentError.cause);
    const cause = causeIsNeo4jError ? currentError.cause : undefined;

    if (causeIsNeo4jError) {
      delete currentError.cause;
    }

    errors.push(currentError);

    if (cause !== undefined) {
      return flattenErrorsToArray(cause, errors);
    }

    return errors;
  };

  const errors = flattenErrorsToArray(cloneDeep(error));
  return errors.reverse();
};
